import {
    ReferenceBusinessId,
    ReferenceContactId,
    RegionId,
    TaskTypeId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { MessageCompose } from "components/messages/message-compose";
import { ReferralTierFilter } from "components/references/dashboard-reference/referral-tier-filter";
import { trpc } from "components/rpc/trpc";
import { Spinner } from "components/ui/spinner";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useState } from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { handleEvent } from "util/user-events";
import { AgendaProvisionBanner, contactPath, orgPath } from "./agenda-actions";
import { AgendaAwaitingOutcome } from "./agenda-awaiting-outcome";
import { AgendaCelebrationCard } from "./agenda-celebration-card";
import { AgendaCompletedToday } from "./agenda-completed-today";
import { AgendaContactCard } from "./agenda-contact-card";
import { AgendaEventOutcomes } from "./agenda-event-outcomes";
import { AgendaHardLandscape } from "./agenda-hard-landscape";
import { AgendaOrgCard } from "./agenda-org-card";
import { AgendaSnoozedSection } from "./agenda-snoozed-section";
import {
    agendaNeedsPhoneProvisioning,
    filterCardsByTier,
    filterOrgCardsByTier,
    mergeAgendaEntries,
} from "./marketing-plan-agenda";
import { bannerKindPrioritizingPlaybook } from "./plan-health";
import { PlanHealthBanner } from "./plan-health-banner";
import { PlanModal } from "./plan-modal";
import { useAgendaCards } from "./use-agenda-cards";
import { useAgendaOrgCards } from "./use-agenda-org-cards";
import { useAgendaRefresh } from "./use-agenda-refresh";
import { usePlanHealth } from "./use-plan-health";

export const AgendaTab: React.FC<unknown> = () => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const invalidateAgenda = useAgendaRefresh();
    const phoneLines = usePhoneLines();
    const [composing, setComposing] = useState<{
        contactId: ReferenceContactId;
        activityTypeId: TaskTypeId | null;
    } | null>(null);
    const [orgComposing, setOrgComposing] = useState<{
        businessId: ReferenceBusinessId;
        activityTypeId: TaskTypeId | null;
    } | null>(null);
    const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const planHealth = usePlanHealth();

    const feedQuery = useQuery({
        ...trpcClient.marketingPlaybook.myAgenda.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    const snoozesQuery = useQuery({
        ...trpcClient.marketingPlaybook.activeSnoozes.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    const hasPhoneLine = phoneLines.length > 0;
    const snoozes = snoozesQuery.data ?? [];
    const cards = useAgendaCards(regionId, feedQuery.data, snoozesQuery.data);
    const orgCards = useAgendaOrgCards(regionId, feedQuery.data, snoozesQuery.data);
    const visibleCards = filterCardsByTier(cards, selectedTierIds);
    const visibleOrgCards = filterOrgCardsByTier(orgCards, selectedTierIds);
    const entries = mergeAgendaEntries(cards, orgCards);
    const visibleEntries = mergeAgendaEntries(visibleCards, visibleOrgCards);
    const showProvisionBanner =
        !feedQuery.isLoading &&
        agendaNeedsPhoneProvisioning(
            [
                ...cards.filter(card => !card.isCelebrating).map(card => card.group),
                ...orgCards.filter(card => !card.isCelebrating).map(card => card.group),
            ],
            hasPhoneLine,
        );

    const openCompose = (
        contactId: ReferenceContactId,
        activityTypeId: TaskTypeId | null,
    ) => {
        handleEvent("marketing-plan-send-email");
        setComposing({ contactId, activityTypeId });
    };

    const closeCompose = () => {
        setComposing(null);
        invalidateAgenda();
    };

    const openOrgCompose = (
        businessId: ReferenceBusinessId,
        activityTypeId: TaskTypeId | null,
    ) => {
        handleEvent("marketing-plan-send-email");
        setOrgComposing({ businessId, activityTypeId });
    };

    const closeOrgCompose = () => {
        setOrgComposing(null);
        invalidateAgenda();
    };

    return (
        <>
            <div className="space-y-4">
                <AgendaHardLandscape />
                <AgendaAwaitingOutcome />
                <AgendaEventOutcomes />
                <div className="flex items-center justify-between gap-3 pt-2">
                    <div className="flex items-center gap-2">
                        <span className="h-6 w-1 flex-shrink-0 rounded-full bg-brand-500" />
                        <h2 className="twoverride text-lg font-semibold text-gray-800">
                            Recommended Actions
                        </h2>
                    </div>
                    <ReferralTierFilter
                        value={selectedTierIds}
                        onChange={setSelectedTierIds}
                    />
                </div>
                <PlanHealthBanner
                    kind={bannerKindPrioritizingPlaybook(planHealth)}
                    onViewPlan={() => setIsPlanModalOpen(true)}
                    setupPlaybookMessage="Once you set up a marketing playbook, we'll show which referral sources are due for outreach here."
                />
                {showProvisionBanner ? <AgendaProvisionBanner /> : null}
                <AgendaCompletedToday />
                {feedQuery.isLoading ? (
                    <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-300">
                        <Spinner />
                    </div>
                ) : entries.length === 0 ? (
                    planHealth.hasNoTouchpoints ? null : (
                        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 ring-1 ring-gray-300">
                            All caught up — no referral sources are due for outreach right
                            now.
                        </div>
                    )
                ) : visibleEntries.length === 0 ? (
                    <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 ring-1 ring-gray-300">
                        No referral sources match the selected tier.
                    </div>
                ) : (
                    <ul className="space-y-3 sm:space-y-4">
                        {visibleEntries.map(entry =>
                            entry.kind === "contact" ? (
                                <li key={entry.card.group.contactId}>
                                    {entry.card.isCelebrating ? (
                                        <AgendaCelebrationCard
                                            to={contactPath(entry.card.group.contactId)}
                                            name={entry.card.group.contactName}
                                        />
                                    ) : (
                                        <AgendaContactCard
                                            group={entry.card.group}
                                            hasPhoneLine={hasPhoneLine}
                                            onSendEmail={openCompose}
                                        />
                                    )}
                                </li>
                            ) : (
                                <li key={entry.card.group.businessId}>
                                    {entry.card.isCelebrating ? (
                                        <AgendaCelebrationCard
                                            to={orgPath(entry.card.group.businessId)}
                                            name={entry.card.group.businessName}
                                        />
                                    ) : (
                                        <AgendaOrgCard
                                            group={entry.card.group}
                                            hasPhoneLine={hasPhoneLine}
                                            onSendEmail={openOrgCompose}
                                        />
                                    )}
                                </li>
                            ),
                        )}
                    </ul>
                )}
                <AgendaSnoozedSection snoozes={snoozes} />
            </div>
            {composing ? (
                <MessageCompose
                    entityId={composing.contactId}
                    entityType={AdvancedSearchEntityType.ReferenceContact}
                    activityTypeId={composing.activityTypeId}
                    onDismiss={closeCompose}
                />
            ) : null}
            {orgComposing ? (
                <MessageCompose
                    entityId={orgComposing.businessId}
                    entityType={AdvancedSearchEntityType.ReferenceBusiness}
                    activityTypeId={orgComposing.activityTypeId}
                    onDismiss={closeOrgCompose}
                />
            ) : null}
            <PlanModal
                isOpen={isPlanModalOpen}
                onDismiss={() => setIsPlanModalOpen(false)}
            />
        </>
    );
};
